import { config } from "@/config";
import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";

/**
 * Absolute app URL for a canvas detail page, for surfaces that live on another
 * origin (the share viewer). Host comes from `config.hosts.app`
 * (`VITE_APP_BASE_URL`); empty config falls back to the production app host —
 * the viewer must never navigate same-origin, its own host has no /canvas route.
 */
export function buildAppCanvasUrl(canvasId: string): string {
  const base = (config.hosts.app || "https://app.cohesive.ai").replace(
    /\/$/,
    "",
  );
  return `${base}${route(ROUTES.CANVAS.DETAIL, { canvasId })}`;
}

/**
 * The canvas id when `url` is a canvas page on this app (its configured host
 * or the origin it is running on), else null. The inverse of
 * {@link buildAppCanvasUrl}, for paste / drop paths that would otherwise
 * scrape the page as a web link.
 */
export function parseAppCanvasUrl(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const hosts = new Set<string>();
  try {
    hosts.add(new URL(config.hosts.app || "https://app.cohesive.ai").host);
  } catch {
    // Unparseable config host: only the current origin counts.
  }
  if (typeof window !== "undefined") hosts.add(window.location.host);
  if (!hosts.has(parsed.host)) return null;
  const prefix = route(ROUTES.CANVAS.DETAIL, { canvasId: "" });
  if (!parsed.pathname.startsWith(prefix)) return null;
  const rest = parsed.pathname.slice(prefix.length).replace(/\/$/, "");
  return rest && !rest.includes("/") ? rest : null;
}
